import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { keyframes } from '@mui/material/styles';

// project imports
import { useNavTheme } from 'contexts/NavThemeContext';

// assets
import { IconHome, IconRefresh, IconAlertTriangle, IconLock, IconCloud, IconError404 } from '@tabler/icons-react';

// ==============================|| ANIMATIONS ||============================== //

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ==============================|| ERROR BOUNDARY ||============================== //

export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  let errorTitle = 'Something went wrong';
  let errorMessage = 'An unexpected error occurred. Please try refreshing the page.';
  let showDetails = false;
  let errorDetails = '';
  let ErrorIcon = IconAlertTriangle;
  let iconColor = '#ef4444';
  let iconBgColor = isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.15)';

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 404:
        errorTitle = 'Page not found';
        errorMessage = "The page you're looking for doesn't exist or has been moved.";
        ErrorIcon = IconError404;
        iconColor = '#6366f1';
        iconBgColor = isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.15)';
        break;
      case 401:
        errorTitle = 'Unauthorized';
        errorMessage = "You don't have permission to access this page.";
        ErrorIcon = IconLock;
        iconColor = '#f59e0b';
        iconBgColor = isLight ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.15)';
        break;
      case 503:
        errorTitle = 'Service unavailable';
        errorMessage = 'Our servers are temporarily down. Please try again in a few minutes.';
        ErrorIcon = IconCloud;
        iconColor = '#8b5cf6';
        iconBgColor = isLight ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.15)';
        break;
      case 418:
        errorTitle = 'Error';
        errorMessage = 'Please contact your administrator for assistance.';
        break;
      default:
        errorTitle = `Error ${error.status}`;
        errorMessage = error.statusText || 'An error occurred while processing your request.';
    }
  } else if (error instanceof Error) {
    showDetails = import.meta.env.DEV;
    errorDetails = error.message;
    if (error.stack && import.meta.env.DEV) {
      errorDetails = error.stack;
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: isLight ? '#fafbfc' : '#0a0b0f',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: isLight
            ? 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }}
      />

      {/* Gradient orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: isLight
            ? 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '15%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: isLight
            ? 'radial-gradient(circle, rgba(239, 68, 68, 0.06) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(239, 68, 68, 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          maxWidth: 520,
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        {/* Icon container */}
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '24px',
            bgcolor: iconBgColor,
            border: `1px solid ${iconColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            animation: `${float} 4s ease-in-out infinite`,
          }}
        >
          <ErrorIcon size={48} style={{ color: iconColor }} />
        </Box>

        {/* Error title */}
        <Typography
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
            fontWeight: 800,
            fontFamily: '"Satoshi", "Inter", sans-serif',
            color: tokens.textPrimary,
            mb: 2,
            letterSpacing: '-0.02em',
          }}
        >
          {errorTitle}
        </Typography>

        {/* Error message */}
        <Typography
          sx={{
            fontSize: '1.05rem',
            color: tokens.textMuted,
            mb: 4,
            lineHeight: 1.7,
            maxWidth: 400,
            mx: 'auto',
          }}
        >
          {errorMessage}
        </Typography>

        {/* Dev error details */}
        {showDetails && errorDetails && (
          <Box
            sx={{
              p: 2.5,
              mb: 4,
              bgcolor: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
              borderRadius: '12px',
              maxHeight: 200,
              overflow: 'auto',
              textAlign: 'left',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#ef4444',
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: '#ef4444',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Development Error
              </Typography>
            </Stack>
            <Typography
              component="pre"
              sx={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.75rem',
                color: tokens.textMuted,
                lineHeight: 1.6,
              }}
            >
              {errorDetails}
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<IconRefresh size={18} />}
            onClick={handleRefresh}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: '0.9rem',
              textTransform: 'none',
              color: tokens.textSecondary,
              borderColor: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
              bgcolor: isLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                borderColor: isLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                bgcolor: isLight ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.06)',
              },
            }}
          >
            Refresh Page
          </Button>
          <Button
            variant="contained"
            startIcon={<IconHome size={18} />}
            onClick={handleGoHome}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: '0.9rem',
              textTransform: 'none',
              bgcolor: '#6366f1',
              color: '#ffffff',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
              '&:hover': {
                bgcolor: '#4f46e5',
                boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
              },
            }}
          >
            Go To Dashboard
          </Button>
        </Stack>

        {/* Subtle help text */}
        <Typography
          sx={{
            mt: 4,
            fontSize: '0.8rem',
            color: tokens.textDimmed,
          }}
        >
          Need help?{' '}
          <Box
            component="a"
            href="mailto:support@synlets.com"
            sx={{
              color: '#6366f1',
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Contact support
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
